import React, { useContext, useEffect, useState } from 'react';
import { Box, Flex, HStack, VStack, Text, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { AccountContext } from '../../../../AccountContext';
import BottomNavBar from '../../../../BottomNavBar';
import ChildHeader from '../../ChildHeader';
import Loading from 'components/Loading';
import { FiCheck, FiChevronDown, FiSearch, FiTrendingUp } from 'react-icons/fi';
import { api } from 'api/api';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

const PAGE_SIZE = 15;

/* ─── helpers ─── */
function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ').filter(Boolean);
  if (p.length === 1) return p[0][0].toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function toMesKey(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function mesLabelFn(dateStr) {
  const d = new Date(dateStr);
  const l = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return l.charAt(0).toUpperCase() + l.slice(1);
}

function formatBRL(v) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateBR(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function isPago(item) {
  return item.pago === true || item.pago === 'true';
}

function isVencida(item) {
  return item.vencida === true || item.vencida === 'true';
}

// 'pago' | 'aguardando' | 'vencido'
function statusPagamento(item) {
  if (isPago(item)) return 'pago';
  if (isVencida(item)) return 'vencido';
  return 'aguardando';
}

const STATUS_CONFIG = {
  pago:       { bg: '#DCFCE7', dot: '#16A34A', color: '#16A34A', label: 'Ativo' },
  aguardando: { bg: '#FFF7ED', dot: '#F97316', color: '#F97316', label: 'Aguardando pagamento' },
  vencido:    { bg: '#FEE2E2', dot: '#DC2626', color: '#DC2626', label: 'Pgto não realizado' },
};

function agrupar(lista) {
  const meses = [];
  lista.forEach((item) => {
    const mk = toMesKey(item.data_cadastro);
    let mes = meses.find((m) => m.mesKey === mk);
    if (!mes) {
      mes = { mesKey: mk, mesLabel: mesLabelFn(item.data_cadastro), items: [] };
      meses.push(mes);
    }
    mes.items.push(item);
  });
  return meses;
}

/* ─── ContratoCard ─── */
function ContratoCard({ item }) {
  const status   = statusPagamento(item);
  const cfg      = STATUS_CONFIG[status];
  const initials = getInitials(item.cliente);
  const formaMap = { pix: 'Pix', boleto: 'Boleto', credit_card: 'Cartão', debit_card: 'Débito' };
  const forma    = formaMap[item.forma_pagamento] || '';

  return (
    <Box bg="white" borderRadius="16px" border="1px solid #EEF1F5" p="14px" w="100%">

      {/* topo: avatar + nome/plano + status */}
      <HStack spacing="12px" alignItems="center">

        {/* avatar */}
        <Flex
          bg="#E8F5F3" borderRadius="21px"
          w="42px" h="42px" minW="42px"
          alignItems="center" justifyContent="center"
        >
          <Text color="#1F7D75" fontSize="14px" fontWeight="700">{initials}</Text>
        </Flex>

        {/* nome + plano */}
        <VStack spacing="2px" alignItems="flex-start" flex={1} minW={0}>
          <Text color="#0F1B2A" fontSize="14px" fontWeight="700" noOfLines={1}>{item.cliente || '—'}</Text>
          <Text color="#5B6B7A" fontSize="11px" fontWeight="500" noOfLines={1}>
            {item.plano || 'Plano'}{forma ? ` • ${forma}` : ''}
          </Text>
        </VStack>

        {/* badge de status */}
        <HStack
          spacing="4px"
          bg={cfg.bg}
          borderRadius="12px"
          px="10px" py="4px"
          flexShrink={0}
        >
          <Box w="6px" h="6px" borderRadius="full" bg={cfg.dot} />
          <Text color={cfg.color} fontSize="11px" fontWeight="600" whiteSpace="nowrap">
            {cfg.label}
          </Text>
        </HStack>
      </HStack>

      {/* divisor */}
      <Box h="1px" bg="#EEF1F5" my="12px" />

      {/* rodapé: data + comissão */}
      <Flex justifyContent="space-between" alignItems="flex-end">
        <VStack spacing="2px" alignItems="flex-start">
          <Text color="#9AA5B1" fontSize="10px" fontWeight="500">Vendido em</Text>
          <Text color="#0F1B2A" fontSize="12px" fontWeight="600">{formatDateBR(item.data_cadastro)}</Text>
        </VStack>
        <VStack spacing="2px" alignItems="flex-end">
          <Text color="#9AA5B1" fontSize="10px" fontWeight="500">Comissão</Text>
          {status === 'pago' && item.valor != null ? (
            <Text color="#16A34A" fontSize="13px" fontWeight="700">+R$ {formatBRL(item.valor)}</Text>
          ) : (
            <Text color={cfg.color} fontSize="12px" fontWeight="600">
              {status === 'vencido' ? 'Não realizado' : 'Pendente'}
            </Text>
          )}
        </VStack>
      </Flex>
    </Box>
  );
}

/* ─── Main ─── */
function Main() {
  const { user, setPage } = useContext(AccountContext);
  const vendedor_id = user?.id_vendedor ;

  useEffect(() => { setPage('Contratos vendidos'); }, []); // eslint-disable-line

  const [allContratos, setAllContratos] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filtro,       setFiltro]       = useState(null);
  const [busca,        setBusca]        = useState('');
  const [limite,       setLimite]       = useState(PAGE_SIZE);

  const fetchContratos = async () => {
    setLoading(true);
    try {
      const res  = await api(`${ENDPOINT}/entrepreneur/contratos-vendidos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendedor_id, status: null }),
      });
      const data = await res.json();
      setAllContratos(data?.success ? data.data : []);
    } catch (e) {
      console.log('fetchContratos error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContratos(); }, []); // eslint-disable-line
  useEffect(() => { setLimite(PAGE_SIZE); }, [filtro, busca]);

  /* ── stats ── */
  const totalContratos = allContratos.length;
  const pagosCount     = allContratos.filter(isPago).length;
  const pendentesCount = allContratos.filter((c) => !isPago(c)).length;

  const hoje     = new Date();
  const valorMes = allContratos
    .filter((c) => {
      if (!isPago(c)) return false;
      const d = new Date(c.data_cadastro);
      return d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear();
    })
    .reduce((s, c) => s + (Number(c.valor) || 0), 0);

  const totalValor  = allContratos.filter(isPago).reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const ticketMedio = pagosCount > 0 ? totalValor / pagosCount : 0;

  /* ── filtro client-side ── */
  const contratosFiltrados = allContratos.filter((c) => {
    if (filtro === 'pago'     && !isPago(c)) return false;
    if (filtro === 'pendente' &&  isPago(c)) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (c.cliente || '').toLowerCase().includes(q) || (c.plano || '').toLowerCase().includes(q);
    }
    return true;
  });

  const contratosPaginados = contratosFiltrados.slice(0, limite);
  const agrupado           = agrupar(contratosPaginados);
  const temMais            = limite < contratosFiltrados.length;
  const restantes          = contratosFiltrados.length - limite;

  const FILTROS = [
    { label: 'Todos',     value: null,       count: totalContratos, countColor: 'white',   countBg: 'rgba(255,255,255,0.25)' },
    { label: 'Ativos',    value: 'pago',     count: pagosCount,     countColor: '#5B6B7A', countBg: '#EEF1F5' },
    { label: 'Pendentes', value: 'pendente', count: pendentesCount, countColor: '#F97316', countBg: '#FFF7ED' },
  ];

  return (
    <Box bg="#F4F7F9" minH="100vh" display="flex" flexDirection="column">

      <ChildHeader />

      <Box overflowY="auto" flex={1} pb="100px">
        <VStack spacing="20px" px="20px" pt="16px" pb="24px" w="100%">

          {/* ── busca ── */}
          <InputGroup w="100%">
            <InputLeftElement pointerEvents="none" h="46px">
              <FiSearch color="#9AA5B1" size={16} />
            </InputLeftElement>
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por cliente ou plano"
              h="46px" bg="white"
              border="1px solid #EEF1F5" borderRadius="14px"
              fontSize="13px" color="#0F1B2A"
              _placeholder={{ color: '#9AA5B1' }}
              _focus={{ borderColor: '#5AB2AE', boxShadow: 'none' }}
              pl="40px"
            />
          </InputGroup>

          {/* ── stats ── */}
          {!loading && (
            <HStack spacing="10px" w="100%" alignItems="stretch">

              <Box flex={1} bg="white" borderRadius="16px" border="1px solid #EEF1F5" p="12px">
                <Flex justifyContent="space-between" alignItems="center" mb="4px">
                  <Text color="#5B6B7A" fontSize="10px" fontWeight="500">Total</Text>
                  <Flex bg="#E8F5F3" borderRadius="8px" w="20px" h="20px" alignItems="center" justifyContent="center">
                    <FiCheck color="#1F7D75" size={11} />
                  </Flex>
                </Flex>
                <Text color="#0F1B2A" fontSize="18px" fontWeight="700" lineHeight="1.2">{totalContratos}</Text>
                <Text color="#9AA5B1" fontSize="10px" fontWeight="500">contratos</Text>
              </Box>

              <Box flex={1} bg="white" borderRadius="16px" border="1px solid #EEF1F5" p="12px">
                <Flex justifyContent="space-between" alignItems="center" mb="4px">
                  <Text color="#5B6B7A" fontSize="10px" fontWeight="500">Este mês</Text>
                  <Flex bg="#DCFCE7" borderRadius="8px" w="20px" h="20px" alignItems="center" justifyContent="center">
                    <FiCheck color="#16A34A" size={11} />
                  </Flex>
                </Flex>
                <Text color="#0F1B2A" fontSize="15px" fontWeight="700" lineHeight="1.2">R$ {formatBRL(valorMes)}</Text>
                <Text color="#9AA5B1" fontSize="10px" fontWeight="500">comissão</Text>
              </Box>

              <Box flex={1} bg="white" borderRadius="16px" border="1px solid #EEF1F5" p="12px">
                <Flex justifyContent="space-between" alignItems="center" mb="4px">
                  <Text color="#5B6B7A" fontSize="10px" fontWeight="500">Ticket médio</Text>
                  <Flex bg="#EFEAFD" borderRadius="8px" w="20px" h="20px" alignItems="center" justifyContent="center">
                    <FiTrendingUp color="#8B5CF6" size={11} />
                  </Flex>
                </Flex>
                <Text color="#0F1B2A" fontSize="15px" fontWeight="700" lineHeight="1.2">R$ {formatBRL(ticketMedio)}</Text>
                <Text color="#9AA5B1" fontSize="10px" fontWeight="500">por contrato</Text>
              </Box>

            </HStack>
          )}

          {/* ── chips ── */}
          <HStack spacing="8px" w="100%" overflowX="auto" pb="2px">
            {FILTROS.map((f) => {
              const ativo = filtro === f.value;
              return (
                <HStack
                  key={String(f.value)}
                  as="button"
                  spacing="6px"
                  px="14px" py="8px"
                  borderRadius="20px"
                  bg={ativo ? '#1F7D75' : 'white'}
                  border="1.5px solid"
                  borderColor={ativo ? '#1F7D75' : '#DDE3EA'}
                  flexShrink={0}
                  cursor="pointer"
                  _active={{ opacity: 0.8 }}
                  onClick={() => setFiltro(f.value)}
                >
                  <Text fontSize="12px" fontWeight="600" color={ativo ? 'white' : '#5B6B7A'}>
                    {f.label}
                  </Text>
                  <Box px="7px" py="1px" borderRadius="10px" bg={ativo ? 'rgba(255,255,255,0.25)' : f.countBg}>
                    <Text fontSize="10px" fontWeight="700" color={ativo ? 'white' : f.countColor}>
                      {f.count}
                    </Text>
                  </Box>
                </HStack>
              );
            })}
          </HStack>

          {/* ── lista ── */}
          {loading ? (
            <Loading />
          ) : contratosFiltrados.length === 0 ? (
            <Text color="#9AA5B1" textAlign="center" mt="20px" fontSize="14px">
              Nenhum contrato encontrado.
            </Text>
          ) : (
            <VStack spacing="14px" w="100%">
              {agrupado.map((mes) => {
                const mesValor = mes.items.filter(isPago).reduce((s, c) => s + (Number(c.valor) || 0), 0);
                return (
                  <Box key={mes.mesKey} w="100%">

                    {/* cabeçalho do mês */}
                    <Flex justifyContent="space-between" alignItems="center" mb="10px">
                      <Text color="#0F1B2A" fontSize="13px" fontWeight="700">{mes.mesLabel}</Text>
                      <Text color="#9AA5B1" fontSize="11px" fontWeight="500">
                        {mes.items.length} {mes.items.length === 1 ? 'contrato' : 'contratos'}
                        {mesValor > 0 ? ` • R$ ${formatBRL(mesValor)}` : ''}
                      </Text>
                    </Flex>

                    {/* cards */}
                    <VStack spacing="10px" w="100%">
                      {mes.items.map((item, i) => (
                        <ContratoCard key={i} item={item} />
                      ))}
                    </VStack>
                  </Box>
                );
              })}

              {/* ver mais */}
              {temMais && (
                <HStack
                  as="button"
                  w="100%"
                  spacing="6px"
                  py="14px"
                  bg="white"
                  borderRadius="16px"
                  border="1.5px solid #DDE3EA"
                  justifyContent="center"
                  cursor="pointer"
                  _active={{ opacity: 0.8 }}
                  onClick={() => setLimite((l) => l + PAGE_SIZE)}
                >
                  <Text fontSize="14px" fontWeight="600" color="#1F7D75">
                    Ver mais ({restantes > PAGE_SIZE ? `+${PAGE_SIZE}` : restantes})
                  </Text>
                  <FiChevronDown color="#1F7D75" size={16} />
                </HStack>
              )}
            </VStack>
          )}

        </VStack>
      </Box>

      <BottomNavBar name="empreendedor" />
    </Box>
  );
}

export default Main;
